import { PropsWithChildren } from 'react'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material'
import ArrowForward from '@mui/icons-material/ArrowForward'
import {
  getFullImageUrl,
  getStandardSrcset,
  isLinkExternal,
  isLinkFile,
} from '@trr/internal-helpers'
import { useSliceStateSelector } from 'slices/hooks'
import { useTranslation } from 'react-i18next'

const ConditionalCardActionArea = ({
  link,
  showActionArea,
  children,
}: PropsWithChildren<{ link: string; showActionArea: boolean }>) => {
  const { t } = useTranslation()
  const externalLink = link ? isLinkExternal(link) : false
  return showActionArea ? (
    <CardActionArea
      data-testid="conditionalCardActionArea"
      target={externalLink ? '_blank' : undefined}
      href={link}
      aria-label={
        externalLink
          ? `${link} ${t('translation:common.openInNewTab.ariaLabel') as string}`
          : link
      }
    >
      {children}
    </CardActionArea>
  ) : (
    <>{children}</>
  )
}

export interface DashboardContentBlockProps {
  content: {
    heading: string
    ingress: string
    linkText?: string
    linkUrl?: string
    imageUrl?: string
    imageAlt?: string
    imagePositionRight?: boolean
    backgroundColor?: string
    buttonVariant?: 'text' | 'outlined' | 'contained'
  }
}

const DashboardContentBlock = ({
  content: {
    heading,
    ingress,
    linkText = undefined,
    linkUrl = undefined,
    imageUrl = undefined,
    imageAlt = undefined,
    imagePositionRight = false,
    backgroundColor = '#fff',
    buttonVariant,
  },
}: DashboardContentBlockProps) => {
  const { MEDIA_URL } = window.APP_CONFIGURATION.COMMON
  const hasLinkData = !!linkUrl && !!linkText
  const { isMobile } = useSliceStateSelector(
    (state) => state.slices.device.deviceSize
  )
  const variant = buttonVariant || 'text'
  const nonTextVariant = variant !== 'text'

  if (imagePositionRight) {
    return (
      <Box mb={3} data-testid="dashboard-content-block-image-right">
        <Card sx={{ backgroundColor }}>
          <ConditionalCardActionArea
            link={
              isLinkFile(linkUrl)
                ? `${MEDIA_URL}${linkUrl ?? ''}`
                : (linkUrl ?? '')
            }
            showActionArea={hasLinkData}
          >
            <CardContent
              sx={{
                minHeight: '100px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack direction="column" flex={3} gap={2}>
                <Typography variant="h5" component="h2">
                  {heading}
                </Typography>
                <Typography variant="body2">{ingress}</Typography>
                {hasLinkData && (
                  <CardActions
                    sx={nonTextVariant ? { p: 0, pt: 2 } : { p: 0, mb: -1 }}
                  >
                    <Button
                      component="span"
                      size={nonTextVariant ? 'medium' : 'small'}
                      variant={variant}
                      sx={{ ...(!nonTextVariant && { ml: -1 }) }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.currentTarget.click()
                        }
                      }}
                    >
                      {linkText}
                      {nonTextVariant && (
                        <ArrowForward sx={{ ml: 1 }} fontSize="small" />
                      )}
                    </Button>
                  </CardActions>
                )}
              </Stack>
              {imageUrl && !isMobile && (
                <Stack flex={1} data-testid="dashboard-content-block-image">
                  <CardMedia
                    component="img"
                    sx={{
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    src={getFullImageUrl(MEDIA_URL, imageUrl, 768)}
                    srcSet={getStandardSrcset(MEDIA_URL, imageUrl, [600])}
                    sizes="(max-width: 600px) 100vw, 600px"
                    alt={imageAlt}
                    loading="lazy"
                  />
                </Stack>
              )}
            </CardContent>
          </ConditionalCardActionArea>
        </Card>
      </Box>
    )
  }

  return (
    <Box mb={3} data-testid="dashboard-content-block-image-top">
      <Card sx={{ backgroundColor }}>
        <ConditionalCardActionArea
          link={
            isLinkFile(linkUrl)
              ? `${MEDIA_URL}${linkUrl ?? ''}`
              : (linkUrl ?? '')
          }
          showActionArea={hasLinkData}
        >
          {imageUrl && (
            <CardMedia
              component="img"
              sx={{ height: { xs: '160px', md: '225px' } }}
              src={getFullImageUrl(MEDIA_URL, imageUrl, 768)}
              srcSet={getStandardSrcset(MEDIA_URL, imageUrl, [600])}
              sizes="(max-width: 600px) 100vw, 600px"
              alt={imageAlt}
              loading="lazy"
            />
          )}
          <CardContent>
            <Typography variant="h5" component="h2" mb={2}>
              {heading}
            </Typography>
            <Typography variant="body1">{ingress}</Typography>
          </CardContent>
          {hasLinkData && (
            <CardActions
              sx={nonTextVariant ? { pl: 3, pt: 2, pb: 3 } : undefined}
            >
              <Button
                component="span"
                size={nonTextVariant ? 'medium' : 'small'}
                variant={variant}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.currentTarget.click()
                  }
                }}
              >
                {linkText}
                {nonTextVariant && (
                  <ArrowForward sx={{ ml: 1 }} fontSize="small" />
                )}
              </Button>
            </CardActions>
          )}
        </ConditionalCardActionArea>
      </Card>
    </Box>
  )
}

export default DashboardContentBlock
